import { Link } from "@/components/LocalizedLink";
import { useTranslation, Trans } from "react-i18next";
import {
  ArrowRight, BookOpen, Target, LayoutDashboard, Sparkles,
  CheckCircle2, Clock, ShieldCheck, Crown,
} from "lucide-react";
import type { User } from "@supabase/supabase-js";
import { usePremium } from "@/hooks/usePremium";
import { CATEGORIES } from "@/lib/questions";
import { categoryLabel } from "@/lib/category-label";
import { PREMIUM_CTA } from "@/lib/cta";

export function LoggedInHome({ user }: { user: User }) {
  const { t } = useTranslation();
  const { premium, until, loading } = usePremium(user.email);

  const displayName =
    (user.user_metadata?.full_name as string | undefined)?.split(" ")[0] ||
    (user.user_metadata?.name as string | undefined)?.split(" ")[0] ||
    user.email?.split("@")[0] ||
    "";

  const untilStr = until
    ? until.toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" })
    : null;

  return (
    <div className="pb-24 sm:pb-0">
      {/* HERO */}
      <section className="surface-gradient">
        <div className="container-page pt-8 sm:pt-16 pb-10 sm:pb-14">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-xs font-medium text-accent">
                <Sparkles className="h-3.5 w-3.5" />
                {premium ? t("bLanding.premiumActive") : t("bLanding.loggedIn")}
              </div>
              <h1 className="mt-3 font-display text-3xl sm:text-5xl font-bold tracking-tight text-balance">
                {t("bLanding.welcome", { name: displayName ? `, ${displayName}` : "" })}
              </h1>
              <p className="mt-3 text-base sm:text-lg text-muted-foreground max-w-xl">
                {t("bLanding.heroLead")}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/practice"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-base font-semibold text-primary-foreground hover:bg-primary/90 shadow-elevated"
                >
                  {t("bLanding.ctaPractice")} <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/exam"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-6 py-3.5 text-base font-medium hover:bg-secondary"
                >
                  {t("bLanding.ctaExam")}
                </Link>
                <Link
                  to="/dashboard"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-6 py-3.5 text-base font-medium hover:bg-secondary"
                >
                  <LayoutDashboard className="h-4 w-4" /> {t("bLanding.ctaDashboard")}
                </Link>
              </div>

              {!loading && (
                <div className="mt-5 text-sm text-muted-foreground">
                  {premium && untilStr ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Crown className="h-4 w-4 text-success" />
                      <Trans i18nKey="bLanding.premiumActiveUntil" values={{ date: untilStr }} components={{ 1: <span className="font-medium text-foreground" /> }}>
                        Premium aktivt till <span className="font-medium text-foreground">{untilStr}</span>
                      </Trans>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5" data-android-hide>
                      <ShieldCheck className="h-4 w-4 text-accent" />
                      {t("bLanding.upgradeLead")}{" "}
                      <Link to={PREMIUM_CTA.to} className="text-accent hover:underline ml-1">{t(PREMIUM_CTA.labelKey)}</Link>
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="lg:col-span-5 grid gap-4">
              <QuickCard
                to="/practice"
                icon={BookOpen}
                title={t("bLanding.qPracticeTitle")}
                text={t("bLanding.qPracticeText")}
              />
              <QuickCard
                to="/exam"
                icon={Target}
                title={t("bLanding.qExamTitle")}
                text={t("bLanding.qExamText")}
              />
              <QuickCard
                to="/dashboard"
                icon={LayoutDashboard}
                title={t("bLanding.qDashboardTitle")}
                text={t("bLanding.qDashboardText")}
              />
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="container-page py-12 sm:py-16">
        <div className="rounded-3xl bg-surface-deep text-primary-foreground p-8 sm:p-12">
          <div className="flex items-center gap-2 text-sm text-primary-foreground/90">
            <BookOpen className="h-4 w-4" /> {t("landing.areasCount")}
          </div>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-bold">{t("bLanding.areasTitle")}</h2>
          <div className="mt-6 flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <Link
                key={c}
                to="/practice"
                className="rounded-full bg-white/10 hover:bg-white/20 backdrop-blur px-3.5 py-1.5 text-sm transition-colors"
              >
                {categoryLabel(c)}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TIPS */}
      <section className="container-page pb-16">
        <div className="grid sm:grid-cols-3 gap-4">
          <TipCard icon={Clock} title={t("bLanding.tipShortTitle")} text={t("bLanding.tipShortText")} />
          <TipCard icon={CheckCircle2} title={t("bLanding.tipRepeatTitle")} text={t("bLanding.tipRepeatText")} />
          <TipCard icon={Target} title={t("bLanding.tipTimerTitle")} text={t("bLanding.tipTimerText")} />
        </div>
      </section>

      {/* STICKY MOBILE CTA */}
      <div className="fixed bottom-0 inset-x-0 z-30 sm:hidden border-t border-border bg-background/95 backdrop-blur p-3">
        <Link
          to="/practice"
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3.5 text-base font-semibold text-primary-foreground shadow-elevated"
        >
          {t("bLanding.ctaPractice")} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

function QuickCard({
  to, icon: Icon, title, text,
}: { to: string; icon: typeof BookOpen; title: string; text: string }) {
  return (
    <Link
      to={to}
      className="group rounded-2xl border border-border bg-card p-5 shadow-card hover:shadow-elevated hover:border-accent/40 transition-all"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-display text-lg font-semibold">{title}</h3>
            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{text}</p>
        </div>
      </div>
    </Link>
  );
}

function TipCard({
  icon: Icon, title, text,
}: { icon: typeof Clock; title: string; text: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent">
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-3 font-display text-base font-semibold">{title}</div>
      <p className="mt-1 text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
